/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useAppData } from '@/hooks/useAppData';
import Skeleton from '@/components/Skeleton/Skeleton';
import Price, { type PriceType } from '@/components/AZCustomComponent/Price';
import InfoToolTip from '@/components/AZCustomComponent/InfoToolTip';
import cx from 'classnames';
import styles from './styles.module.scss';
import { Label } from '@/features/i18n';
import { useLabels } from '@/hooks/useLabels';
import azCommonStyles from '@/theme/globals.module.scss';
import { formatPrice } from '@/utils/validator/formatPrice';
import PriceV2 from '@/components/AZCustomComponent/Price/PriceV2';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';

const labelMap = {
  lblRegularPrice: 'label_regular_price',
  label_regularPrice_camelCase: 'label_regularPrice_camelCase',
  lblOrderSummaryCoreDeposit: 'label_cart_orderSummary_coreDeposit',
  lblRefundable: 'label_refundable',
  labelRefundableCoreCharge: 'label_refundable_core_charge',
  lbllineItemWhatIsIt: 'label_cart_lineItem_WhatIsIt',
  lblCoreDetails: 'pagecontent_cart_corePopup_CoreDetails',
  labelRegularPricePlusCore: 'label_regular_price_plus_core',
};

type Props = {
  totalCorePrice: number;
  isBopusMexicoStoreEnabled: boolean;
  totalPrice: string;
  dealPrice: string[];
  unsetMaxWidth?: boolean;
};

const CorePriceInfoPopOver = ({
  totalCorePrice,
  isBopusMexicoStoreEnabled,
  totalPrice,
  dealPrice,
  unsetMaxWidth,
}: Props) => {
  const labels = useLabels(labelMap);
  const { appData } = useAppData();
  const { isFetchingLocation } = appData;
  const locale = useLocale();
  const isMX = locale === countryCodes.mx;
  const getCorePrice = (corePrice: string) => {
    const formattedPrice = formatPrice(Number(corePrice));
    if (formattedPrice !== 0 && formattedPrice !== null) {
      return formattedPrice.split('.');
    }
  };
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const havePrice = !!dealPrice.reduce((p, c) => p + Number(c || '0'), 0);

  const coreRefundLabel = isBopusMexicoStoreEnabled
    ? `${labels.lblOrderSummaryCoreDeposit}${' '}
${labels.lblRefundable}${' '}`
    : labels.labelRefundableCoreCharge;

  if (!isFetchingLocation && !havePrice) {
    return <NoPrice />;
  }

  return (
    <div
      className={cx(styles.corePriceWrapper, {
        [styles.unsetMaxWidth]: unsetMaxWidth,
      })}
    >
      <div
        className={cx(styles.corePriceRow, {
          [styles.addMargin]: !isProductDetailsPageV2Enabled,
        })}
      >
        <div
          className={cx(styles.breakdownTitle, {
            [styles.pdpRedesign]: isProductDetailsPageV2Enabled,
            [styles.pdpRedesignMX]: isProductDetailsPageV2Enabled && isMX,
          })}
        >
          {isProductDetailsPageV2Enabled
            ? labels.label_regularPrice_camelCase
            : labels.lblRegularPrice}
        </div>

        <PriceOrSkeleton
          priceType={totalCorePrice ? 'secondary' : 'strikethrough'}
          isFetchingLocation={isFetchingLocation}
          dealPrice={dealPrice}
          showPlus={false}
        />
      </div>
      <div
        className={cx(styles.coreChargeWrapper, {
          [styles.pdpRedesign]: isProductDetailsPageV2Enabled,
        })}
      >
        <div
          className={cx(styles.corePriceRow, styles.pbottom, {
            [styles.starcColorPrimary]: isProductDetailsPageV2Enabled,
          })}
        >
          <div>
            <span className={styles.corePriceLabel}>{coreRefundLabel}</span>
            <InfoToolTip
              classes={{ icon: styles.infoTooltip }}
              header={labels.lbllineItemWhatIsIt}
              body={labels.lblCoreDetails}
              divStyle={{
                display: 'inline-block',
                position: 'relative',
                verticalAlign: 'middle',
                lineHeight: '1',
              }}
            />
          </div>
          <PriceOrSkeleton
            isFetchingLocation={isFetchingLocation}
            dealPrice={getCorePrice(totalCorePrice.toString()) || []}
            showPlus
            priceType="secondary"
            type="core"
          />
        </div>
        <div
          className={cx(styles.corePriceRowSeperator, {
            [styles.newPdpDesign]: isProductDetailsPageV2Enabled,
          })}
        >
          <p
            className={cx({
              [styles.regularPricePlusCore]: isProductDetailsPageV2Enabled,
            })}
          >
            {labels.labelRegularPricePlusCore}
          </p>
          <div className={cx({ [styles.starcColorPrimary]: isProductDetailsPageV2Enabled })}>
            <PriceOrSkeleton
              isFetchingLocation={isFetchingLocation}
              dealPrice={getCorePrice(totalPrice) || null}
              showPlus={false}
              priceType="secondary"
              type="core"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorePriceInfoPopOver;

const PriceOrSkeleton = ({
  isFetchingLocation,
  dealPrice,
  showPlus,
  priceType,
  type = undefined,
}: {
  isFetchingLocation: boolean;
  dealPrice: string[] | null;
  showPlus: boolean;
  priceType: PriceType;
  type?: string | undefined;
}) => {
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  return isFetchingLocation ? (
    <Skeleton
      className={styles.refundablePriceSkeleton}
      width={50}
      height={16}
      animation={'wave'}
    />
  ) : isProductDetailsPageV2Enabled ? (
    <PriceV2 priceType={priceType} dealPrice={dealPrice} type={type} showPlus={showPlus} />
  ) : (
    <Price priceType={priceType} dealPrice={dealPrice} type={type} showPlus={showPlus} />
  );
};

const NoPrice = () => (
  <div data-testid="price-fragment">
    <span className={cx(azCommonStyles['az-title-5-heavy'])}>
      <Label label="label_shelf_resultsList_price_not_available" />
    </span>
  </div>
);
